import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@mui/material';

import {
  AutocompleteSelectInput,
  CtaButton,
  ModalContainer,
  SelectInput,
  TextInput,
} from '@tassomutuo-backoffice/ui';
import {
  formatNumberWithThousandsSeparator,
  removeThousandSeparator,
} from '../../../../utils/formatMoney';

interface IProps {
  open: boolean;
  handleClose: () => void;
  banks: string[];
  purposesLabelsValues: {
    label: string;
    value: string;
  }[];
  onSubmitQuoteForm: (data: IFormInputs) => Promise<void>;
}

interface IFormInputs {
  bank: string;
  rateType: string;
  duration: string;
  age: string;
  purpose: string;
  loanAmount: string;
  propertyValue: string;
}

const formDefaultValues = {
  bank: 'Tutte',
  rateType: 'fisso',
  duration: '15',
  age: '',
  purpose: 'Acquistoprimacasa',
  loanAmount: '',
  propertyValue: '',
};

const rateTypeSelectOptions = [
  { label: 'Fisso', value: 'fisso' },
  { label: 'Variabile', value: 'variabile' },
];

const durationSelectOptions = [
  { label: '5', value: '5' },
  { label: '10', value: '10' },
  { label: '15', value: '15' },
  { label: '20', value: '20' },
  { label: '25', value: '25' },
  { label: '30', value: '30' },
];

const QuoteModalForm = ({
  open,
  handleClose,
  banks,
  purposesLabelsValues,
  onSubmitQuoteForm,
}: IProps) => {
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<IFormInputs>({ defaultValues: formDefaultValues });

  const [isLoading, setIsLoading] = useState(false);

  const onCloseHandler = () => {
    handleClose();
    reset(formDefaultValues);
  };

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    const dataToSend = {
      ...data,
      loanAmount: removeThousandSeparator(data.loanAmount),
      propertyValue: removeThousandSeparator(data.propertyValue),
    };
    try {
      setIsLoading(true);
      await onSubmitQuoteForm(dataToSend);
      onCloseHandler();
    } finally {
      setIsLoading(false);
    }
  };

  const banksSelectOption = [
    { label: 'Tutte', value: 'Tutte' },
    ...(banks ?? []).map((bankName) => ({
      label: bankName,
      value: bankName,
    })),
  ];

  return (
    <ModalContainer
      open={open}
      handleClose={onCloseHandler}
      title="Preventivo"
      containerSx={{ maxWidth: '800px' }}
      mtHeader={0}
    >
      <Box
        width="100%"
        component="form"
        maxHeight="60vh"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box display="flex" gap={2}>
          <Controller
            control={control}
            name="bank"
            defaultValue="Tutte"
            render={({ field }) => (
              <AutocompleteSelectInput
                label="Banca"
                helperText={errors?.bank?.message}
                error={!!errors?.bank}
                selectOptions={banksSelectOption}
                {...field}
                value={banksSelectOption.find((p) => p.value === field.value)}
                /* eslint-disable-next-line */
                // @ts-expect-error
                onChange={(
                  event: React.SyntheticEvent,
                  /* eslint-disable-next-line */
                  value: any,
                  reason: string,
                  details?: string
                ) => {
                  field.onChange(value?.value || '');
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="rateType"
            rules={{
              required: 'Campo obbligatorio',
            }}
            render={({ field }) => (
              <SelectInput
                label="Tasso"
                helperText={errors?.rateType?.message}
                error={!!errors?.rateType}
                selectOptions={rateTypeSelectOptions}
                {...field}
              />
            )}
          />
        </Box>
        <Box display="flex" gap={2}>
          <Controller
            control={control}
            name="duration"
            rules={{
              required: 'Campo obbligatorio',
            }}
            render={({ field }) => (
              <SelectInput
                label="Durata"
                helperText={errors?.duration?.message}
                error={!!errors?.duration}
                selectOptions={durationSelectOptions}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="age"
            rules={{
              required: 'Campo obbligatorio',
              min: { value: 18, message: "Inserisci un'età valida" },
              max: { value: 99, message: "Inserisci un'età valida" },
            }}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                type="number"
                label="Età"
                helperText={errors?.age?.message}
                error={!!errors?.age}
                {...field}
              />
            )}
          />
        </Box>
        <Box display="flex" gap={2}>
          <Controller
            control={control}
            name="loanAmount"
            rules={{
              required: 'Campo obbligatorio',
            }}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Importo mutuo"
                helperText={errors?.loanAmount?.message}
                error={!!errors?.loanAmount}
                showEuroIcon
                {...field}
                onChange={(e: { target: { value: string } }) =>
                  field.onChange(
                    formatNumberWithThousandsSeparator(e.target.value)
                  )
                }
                value={formatNumberWithThousandsSeparator(field.value)}
              />
            )}
          />
          <Controller
            control={control}
            name="propertyValue"
            rules={{
              required: 'Campo obbligatorio',
            }}
            render={({ field }) => (
              <TextInput
                type="text"
                label="Valore immobile"
                helperText={errors?.propertyValue?.message}
                error={!!errors?.propertyValue}
                showEuroIcon
                {...field}
                onChange={(e: { target: { value: string } }) =>
                  field.onChange(
                    formatNumberWithThousandsSeparator(e.target.value)
                  )
                }
                value={formatNumberWithThousandsSeparator(field.value)}
              />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          <Controller
            control={control}
            name="purpose"
            rules={{
              required: 'Campo obbligatorio',
            }}
            render={({ field }) => (
              <SelectInput
                label="Finalità"
                helperText={errors?.purpose?.message}
                error={!!errors?.purpose}
                selectOptions={purposesLabelsValues}
                {...field}
              />
            )}
          />
        </Box>
        <Box display="flex" justifyContent="center" my={4}>
          <CtaButton variant="contained" type="submit" loading={isLoading}>
            SALVA
          </CtaButton>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export default QuoteModalForm;
